import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router-dom'
import { LOGIN_MUTATION } from '../graphql/operations'
import { useAuth } from '../auth/AuthContext'

interface LoginResponse {
  userLogin: {
    token: string | null
    user: {
      id: string
      email: string
      fullName: string
      role: string
      canSell: boolean
      canManageMall: boolean
      mall: { id: string; name: string } | null
      store: { id: string; name: string } | null
    } | null
    userErrors: { field?: string[]; message: string }[]
  }
}

export default function Login() {
  // Form starts empty. Earlier versions pre-filled with a seeded
  // dev-account email/password to save typing during local development.
  // Removed because:
  //   1. Credentials were committed in plaintext alongside source.
  //   2. Apollo client used to default to the production endpoint, so
  //      hitting "Sign in" with the seeded creds attempted prod auth
  //      from any developer's machine.
  //   3. Anyone who opened the built studio saw the dev email +
  //      password rendered in the inputs.
  // Local development convenience can come from the browser's password
  // manager once the dev account is saved.
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { setSession } = useAuth()
  const [login, { loading }] = useMutation<LoginResponse>(LOGIN_MUTATION)

  async function loginWith(em: string, pw: string, dest?: string) {
    setError(null)
    try {
      const { data } = await login({ variables: { email: em, password: pw } })
      const payload = data?.userLogin
      const userErr = payload?.userErrors?.[0]?.message
      if (userErr) { setError(userErr); return }
      if (payload?.token && payload.user) {
        const u = payload.user
        setSession(payload.token, {
          id: u.id,
          email: u.email,
          fullName: u.fullName,
          role: u.role,
          storeId: u.store?.id,
          storeName: u.store?.name,
          mallId: u.mall?.id,
          mallName: u.mall?.name,
          canSell: u.canSell,
          canManageMall: u.canManageMall
        })
        // Sellers/managers land in the management area; shoppers go to the shop.
        const from = (location.state as { from?: { pathname: string } } | undefined)?.from?.pathname
        const home = (u.canSell || u.canManageMall) ? '/manage/store' : '/shop'
        navigate(dest ?? from ?? home, { replace: true })
      } else {
        setError('Login succeeded but server returned no token.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    void loginWith(email, password)
  }

  return (
    <div className="login-card">
      <h1>Sign in</h1>
      <p className="muted">Shop, or broadcast as a seller.</p>

      <form onSubmit={handleSubmit}>
        <label>
          <span>Email</span>
          <input
            type="email"
            value={email}
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          <span>Password</span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
